import { z } from 'zod';
import { CnyAmount } from './money.js';

// A synchronous image response can take over 90 seconds. Each enclosing
// deadline must outlive its child, including bounded storage and DB work.
export const IMAGE_EXECUTION_LIMITS = Object.freeze({submissionMs:600_000,brokerMs:750_000,leaseSeconds:780,objectMs:30_000});

// K tiers describe the longest edge. Keep the concrete dimensions shared by
// the catalogue and editor; the Images API still receives WIDTHxHEIGHT.
export const IMAGE_SIZE_PRESETS = [
  {ratio:'1:1',pixels:'1K',size:'1024x1024'},
  {ratio:'4:3',pixels:'1K',size:'1024x768'},
  {ratio:'3:4',pixels:'1K',size:'768x1024'},
  {ratio:'16:9',pixels:'1K',size:'1024x576'},
  {ratio:'9:16',pixels:'1K',size:'576x1024'},
  {ratio:'1:1',pixels:'2K',size:'2048x2048'},
  {ratio:'4:3',pixels:'2K',size:'2048x1536'},
  {ratio:'3:4',pixels:'2K',size:'1536x2048'},
  {ratio:'16:9',pixels:'2K',size:'2048x1152'},
  {ratio:'9:16',pixels:'2K',size:'1152x2048'},
  {ratio:'1:1',pixels:'4K',size:'4096x4096'},
  {ratio:'4:3',pixels:'4K',size:'4096x3072'},
  {ratio:'3:4',pixels:'4K',size:'3072x4096'},
  {ratio:'16:9',pixels:'4K',size:'4096x2304'},
  {ratio:'9:16',pixels:'4K',size:'2304x4096'},
] as const;

export function imageSizeDetails(size:string) {
  const preset=IMAGE_SIZE_PRESETS.find(p=>p.size===size);
  if(preset)return preset;
  const match=/^([1-9]\d*)x([1-9]\d*)$/.exec(size);
  if(!match)return {size,ratio:'',pixels:''};
  const width=Number(match[1]),height=Number(match[2]);
  let a=width,b=height;while(b){const remainder=a%b;a=b;b=remainder;}
  return {size,ratio:`${width/a}:${height/a}`,pixels:size};
}

export const MediaId = z.uuid();
export const ImageInput = z.strictObject({
  model: z.string().min(1).max(120), prompt: z.string().trim().min(1).max(4000),
  size: z.string().regex(/^\d{2,4}x\d{2,4}$/), n: z.literal(1),
  referenceAssetIds: z.array(MediaId).max(4).default([]),
});
export const UploadIntentInput = z.strictObject({
  name: z.string().min(1).max(160), mime: z.enum(['image/png','image/jpeg','image/webp']),
  bytes: z.number().int().positive().max(8 * 1024 * 1024), sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export const ImageJobInput = z.strictObject({quoteId:MediaId, connectionId:MediaId, authorizeReadArchive:z.literal(true)});
// Synchronous fee observation uses the signed-in account session; a model
// connection is needed only for an existing task's artifact/read permission.
export const ImageResumeInput = z.strictObject({connectionId:MediaId.optional(), authorizeReadArchive:z.literal(true)});
export const ImagePrice = z.strictObject({
  model:z.string().min(1).max(120), label:z.string().min(1).max(120), group:z.string().min(1).max(80),
  plugin:z.string().regex(/^[a-z0-9_-]{1,64}$/), sizes:z.array(z.string().regex(/^\d{2,4}x\d{2,4}$/)).min(1).max(20),
  protocol:z.enum(['task_plugin','openai_image']).default('task_plugin'),
  maxReferences:z.number().int().min(0).max(4), estimated:CnyAmount,
  capabilityVersion:z.string().min(1).max(100), priceVersion:z.string().min(1).max(100),
  sourceRevision:z.string().min(1).max(120), observedAt:z.iso.datetime(),
});
// Existing P3 catalogue response, shared with the integrated creation UI.
export const ImageModelOption = ImagePrice.omit({plugin:true,sourceRevision:true}).extend({currency:z.literal('CNY'),unit:z.literal('yuan'),n:z.array(z.literal(1))});
export const ImageOptions = z.object({models:z.array(ImageModelOption),upload:z.object({maxBytes:z.number().int().positive(),mimes:z.array(z.enum(['image/png','image/jpeg','image/webp']))})});
export const ImageConnectionRef = z.object({id:MediaId,model:z.string(),group:z.string()});
export const ImagePendingSubmission = z.strictObject({key:z.uuid(),quoteId:MediaId,connectionId:MediaId});
export const ImageQuote = z.strictObject({
  id:MediaId, requestDigest:z.string(), capabilityVersion:z.string(), priceVersion:z.string(),
  currency:z.literal('CNY'), unit:z.literal('yuan'), estimated:CnyAmount, expiresAt:z.iso.datetime(),
});
export const ImageTaskState = z.enum(['queued','submitting','running','succeeded','failed','canceled','outcome_unknown']);
export const ImageArchiveState = z.enum(['pending','archiving','ready','retry','unavailable']);
export const ImageReconciliationState = z.enum(['pending','observed','authorization_required','outcome_unknown']);
export const MediaAsset = z.strictObject({
  id:MediaId, name:z.string(), mime:z.string(), bytes:z.number().int(), sha256:z.string(),
  width:z.number().int(), height:z.number().int(), source:z.enum(['upload','work']), contentUrl:z.string(),
});
export const ImageJob = z.object({
  id:MediaId, quoteId:MediaId, taskId:z.string().nullable(), upstreamRequestId:z.string().nullable(), taskStatus:ImageTaskState,
  archiveStatus:ImageArchiveState, reconciliationStatus:ImageReconciliationState,
  rejectionStatus:z.number().int().min(400).max(599).nullable().default(null),
  rejectionReason:z.enum(['channel_unavailable']).nullable().default(null),
  currency:z.literal('CNY'), unit:z.literal('yuan'), estimated:CnyAmount,
  fee:z.object({state:z.enum(['unobserved','pending','net_observed']), net:CnyAmount.nullable(), charged:CnyAmount.nullable(), refunded:z.null(), source:z.string().nullable().default(null), observedAt:z.iso.datetime().nullable().default(null), externalInvoice:z.literal('not_reconciled')}),
  createdAt:z.iso.datetime(), updatedAt:z.iso.datetime(), canCancel:z.boolean(),
  observation:z.object({kind:z.enum(['fee','task']),model:z.string(),group:z.string(),canResume:z.boolean()}),
  workId:MediaId.nullable(), assets:z.array(MediaAsset),
  input:ImageInput.optional(),
  prompt:z.string().max(4000).default(''),referenceAssets:z.array(MediaAsset).max(4).default([]),
});
// Resolving an uncertain submission never generates. A rejected resolution
// durably fences this exact key before the editor can request a new quote.
export const ImageSubmissionResolution = z.discriminatedUnion('state',[
  z.object({state:z.literal('accepted'),job:ImageJob}),
  z.object({state:z.literal('rejected'),code:z.literal('SUBMISSION_NOT_ACCEPTED')}),
]);
export const CreationSessionSnapshot = z.object({authenticated:z.literal(true),realm:z.literal('site'),accountId:MediaId,siteId:z.string().min(1),source:z.string().min(1),csrf:z.string().min(1),sessionEpoch:z.string().regex(/^[a-f0-9]{64}$/)});
